import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { COLORS, LANDMARK_KEYS, LANDMARK_LABELS, MEASUREMENT_LINES } from "./editorColors";
import styles from "./CranialCanvas.module.css";

// Screen-pixel sizes (converted to viewBox units per axis at render time).
const HANDLE_RADIUS = { active: 8, selected: 11, idle: 3, landmark: 11, landmarkIdle: 7, derived: 7, scale: 8 };
const HIT_RADIUS = { touch: 24, mouse: 12 };
const SEGMENT_HIT = { touch: 16, mouse: 8 };
const DRAG_SLOP = { touch: 8, mouse: 4 };
const DOUBLE_TAP_MS = 350;

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const isPoint = (p) => p && Number.isFinite(p.x) && Number.isFinite(p.y);
const isTouchLike = (pointerType) => pointerType === "touch" || pointerType === "pen";

const toPath = (points) =>
  points.length ? `M ${points.map((p) => `${p.x} ${p.y}`).join(" L ")} Z` : "";

function distToSegmentSq(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  const t = lenSq > 0 ? Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq)) : 0;
  const cx = a.x + t * dx - p.x;
  const cy = a.y + t * dy - p.y;
  return cx * cx + cy * cy;
}

/** Line drawn twice (dark halo + color) so it reads over any photo. */
function OverlayLine({ line, color, width = 2.5, dashed = false }) {
  if (!line || !isPoint(line.start) || !isPoint(line.end)) return null;
  const common = {
    x1: line.start.x,
    y1: line.start.y,
    x2: line.end.x,
    y2: line.end.y,
    vectorEffect: "non-scaling-stroke",
    strokeLinecap: "round",
  };
  return (
    <g>
      <line {...common} stroke={COLORS.halo} strokeWidth={width + 2.5} />
      <line {...common} stroke={color} strokeWidth={width} strokeDasharray={dashed ? "6 5" : undefined} />
    </g>
  );
}

/** Circle of `r` screen pixels drawn as an ellipse in the anisotropic 0..1 viewBox. */
function Handle({ point, r, unit, fill, stroke, strokeWidth = 2 }) {
  if (!isPoint(point)) return null;
  return (
    <ellipse
      cx={point.x}
      cy={point.y}
      rx={r * unit.x}
      ry={r * unit.y}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Photo with an SVG overlay in normalized coordinates (viewBox 0 0 1 1, preserveAspectRatio
 * none). Handles are sized in screen pixels via a ResizeObserver; strokes use
 * non-scaling-stroke. Hit-testing is done in JS in screen pixels, so overlapping handles
 * resolve to the nearest one and touch gets larger targets.
 * Zoom/pan: the stage is sized zoom x viewport and offset by `pan` (viewport fractions).
 */
function CranialCanvas({
  imageUrl,
  imageWidth,
  imageHeight,
  geometry,
  calibration,
  measurements,
  aiSuggestion = null,
  mode = "pan",
  selectedPointIndex = null,
  zoom = 1,
  pan = { x: 0, y: 0 },
  readOnly = false,
  showAnnotations = true,
  maxHeight = "70vh",
  alt = "Fotografia do crânio vista de cima",
  className = "",
  onDragStart,
  onDragMove,
  onDragEnd,
  onPointTap,
  onPointDoubleTap,
  onSegmentTap,
  onBackgroundTap,
  onZoom,
  onPan,
}) {
  const viewportRef = useRef(null);
  const stageRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const lastTapRef = useRef({ key: null, at: 0 });
  const onZoomRef = useRef(onZoom);
  onZoomRef.current = onZoom;

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Wheel zoom needs a non-passive listener to prevent page scroll.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || readOnly) return undefined;
    const handleWheel = (e) => {
      if (!onZoomRef.current) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * 0.0015);
      onZoomRef.current(factor, {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      });
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [readOnly]);

  const contour = (geometry?.cranialContour || []).filter(isPoint);
  const landmarks = geometry?.landmarks || {};
  const scalePoints = (calibration?.points || []).filter(isPoint);
  const lines = measurements && measurements.ok ? measurements.lines : null;
  // A is always derived by computeMeasurements (axis crossing with the contour). P is drawn at
  // the derived point while landmarks.posterior is null (automatic) and can be dragged: the
  // first drag stores it in landmarks.posterior (manual).
  const derived = measurements && measurements.ok && measurements.derived ? measurements.derived : {};
  const posteriorManual = isPoint(landmarks.posterior);
  const posteriorPoint = posteriorManual ? landmarks.posterior : derived.posterior;
  const ghost =
    !readOnly && geometry?.wasManuallyEdited && Array.isArray(aiSuggestion?.cranialContour)
      ? aiSuggestion.cranialContour.filter(isPoint)
      : [];

  const stageW = size.w * zoom;
  const stageH = size.h * zoom;
  const unit = { x: stageW > 0 ? 1 / stageW : 0, y: stageH > 0 ? 1 / stageH : 0 };
  const ready = stageW > 0 && stageH > 0;

  // ---------- pointer math ----------

  const toNorm = (clientX, clientY) => {
    const rect = stageRef.current.getBoundingClientRect();
    return {
      x: clamp01((clientX - rect.left) / rect.width),
      y: clamp01((clientY - rect.top) / rect.height),
    };
  };

  const toScreen = (p, rect) => ({ x: rect.left + p.x * rect.width, y: rect.top + p.y * rect.height });

  const hitTest = (clientX, clientY, pointerType) => {
    if (mode === "pan") return null;
    const rect = stageRef.current.getBoundingClientRect();
    const touch = isTouchLike(pointerType);
    const radius = touch ? HIT_RADIUS.touch : HIT_RADIUS.mouse;
    const c = { x: clientX, y: clientY };
    let candidates = [];
    if (mode === "landmarks") {
      candidates = LANDMARK_KEYS.filter((key) => isPoint(landmarks[key])).map((key) => ({
        target: { kind: "landmark", key },
        point: landmarks[key],
      }));
      if (isPoint(posteriorPoint)) {
        candidates.push({ target: { kind: "landmark", key: "posterior" }, point: posteriorPoint });
      }
    } else if (mode === "contour") {
      candidates = contour.map((point, index) => ({ target: { kind: "contour", index }, point }));
    } else if (mode === "scale") {
      candidates = scalePoints.map((point, index) => ({ target: { kind: "scale", index }, point }));
    }

    let best = null;
    let bestDist = radius * radius;
    candidates.forEach((cand) => {
      const s = toScreen(cand.point, rect);
      const d = (s.x - c.x) ** 2 + (s.y - c.y) ** 2;
      if (d <= bestDist) {
        bestDist = d;
        best = cand;
      }
    });
    if (best) return { type: "handle", ...best };

    if (mode === "contour" && contour.length >= 2) {
      const segRadius = touch ? SEGMENT_HIT.touch : SEGMENT_HIT.mouse;
      let segBest = null;
      let segDist = segRadius * segRadius;
      contour.forEach((p, i) => {
        const d = distToSegmentSq(c, toScreen(p, rect), toScreen(contour[(i + 1) % contour.length], rect));
        if (d <= segDist) {
          segDist = d;
          segBest = i;
        }
      });
      if (segBest !== null) return { type: "segment", index: segBest };
    }
    return null;
  };

  const pinchState = () => {
    const [a, b] = Array.from(pointersRef.current.values());
    return { dist: Math.hypot(b.x - a.x, b.y - a.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
  };

  const targetKey = (t) => `${t.kind}:${t.key !== undefined ? t.key : t.index}`;

  // ---------- pointer handlers ----------

  const handlePointerDown = (e) => {
    if (readOnly || !stageRef.current) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    try {
      viewportRef.current.setPointerCapture(e.pointerId);
    } catch (err) {
      // Pointer capture is best-effort (unsupported on some old browsers).
    }
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 2) {
      const g = gestureRef.current;
      if (g && g.type === "drag" && onDragEnd) onDragEnd(g.target);
      gestureRef.current = { type: "pinch", ...pinchState() };
      return;
    }
    if (pointersRef.current.size > 2) return;

    const start = { x: e.clientX, y: e.clientY };
    const base = { start, last: start, moved: false, pointerType: e.pointerType };
    const hit = hitTest(e.clientX, e.clientY, e.pointerType);
    if (hit && hit.type === "handle") {
      const norm = toNorm(e.clientX, e.clientY);
      gestureRef.current = {
        ...base,
        type: "drag",
        target: hit.target,
        offset: { x: hit.point.x - norm.x, y: hit.point.y - norm.y },
      };
      if (onDragStart) onDragStart(hit.target);
    } else if (hit && hit.type === "segment") {
      gestureRef.current = { ...base, type: "segment", index: hit.index };
    } else {
      gestureRef.current = { ...base, type: "background" };
    }
  };

  const handlePointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gestureRef.current;
    if (!g) return;

    if (g.type === "pinch") {
      if (pointersRef.current.size < 2) return;
      const next = pinchState();
      const rect = viewportRef.current.getBoundingClientRect();
      if (onPan) onPan((next.mid.x - g.mid.x) / rect.width, (next.mid.y - g.mid.y) / rect.height);
      if (onZoom && g.dist > 0) {
        onZoom(next.dist / g.dist, {
          x: (next.mid.x - rect.left) / rect.width,
          y: (next.mid.y - rect.top) / rect.height,
        });
      }
      gestureRef.current = { ...g, ...next };
      return;
    }

    const slop = isTouchLike(g.pointerType) ? DRAG_SLOP.touch : DRAG_SLOP.mouse;
    if (!g.moved && Math.hypot(e.clientX - g.start.x, e.clientY - g.start.y) > slop) {
      g.moved = true;
      if (g.type === "segment") g.type = "background";
    }
    if (!g.moved) return;

    if (g.type === "drag") {
      const norm = toNorm(e.clientX, e.clientY);
      if (onDragMove) {
        onDragMove(g.target, { x: clamp01(norm.x + g.offset.x), y: clamp01(norm.y + g.offset.y) });
      }
    } else if (g.type === "background" && onPan) {
      const rect = viewportRef.current.getBoundingClientRect();
      onPan((e.clientX - g.last.x) / rect.width, (e.clientY - g.last.y) / rect.height);
      g.last = { x: e.clientX, y: e.clientY };
    }
  };

  const finishPointer = (e, cancelled) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.delete(e.pointerId);
    const g = gestureRef.current;
    if (!g) return;
    if (g.type === "pinch") {
      // The remaining finger is ignored until it is lifted.
      if (pointersRef.current.size === 0) gestureRef.current = null;
      return;
    }
    gestureRef.current = null;
    const tapped = !cancelled && !g.moved;

    if (g.type === "drag") {
      if (onDragEnd) onDragEnd(g.target);
      if (!tapped) return;
      const key = targetKey(g.target);
      const now = Date.now();
      const isDouble = lastTapRef.current.key === key && now - lastTapRef.current.at < DOUBLE_TAP_MS;
      lastTapRef.current = { key: isDouble ? null : key, at: now };
      if (isDouble && onPointDoubleTap) onPointDoubleTap(g.target);
      else if (onPointTap) onPointTap(g.target);
    } else if (tapped && g.type === "segment") {
      if (onSegmentTap) onSegmentTap(g.index, toNorm(e.clientX, e.clientY));
    } else if (tapped && g.type === "background") {
      if (onBackgroundTap) onBackgroundTap(toNorm(e.clientX, e.clientY));
    }
  };

  // ---------- render ----------

  const ratio = imageWidth > 0 && imageHeight > 0 ? imageWidth / imageHeight : 4 / 3;
  const viewportStyle = {
    aspectRatio: `${ratio}`,
    maxWidth: `calc(${maxHeight} * ${ratio})`,
  };
  const stageStyle = {
    left: `${pan.x * 100}%`,
    top: `${pan.y * 100}%`,
    width: `${zoom * 100}%`,
    height: `${zoom * 100}%`,
  };
  const modeClass = readOnly ? styles.readOnly : styles[`mode_${mode}`] || "";

  const contourActive = !readOnly && mode === "contour";
  const landmarksActive = !readOnly && mode === "landmarks";
  const scaleActive = !readOnly && mode === "scale";

  /** Letter over a point; the long name only on the draggable landmarks while "Marcos" is active. */
  const renderLandmarkLabel = (key, point, draggable) => {
    if (!isPoint(point)) return null;
    const active = draggable && landmarksActive;
    return (
      <div
        key={key}
        className={`${styles.landmarkLabel} ${active ? styles.landmarkLabelActive : ""}`}
        style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
      >
        <span className={styles.landmarkLetter}>{LANDMARK_LABELS[key].short}</span>
        {active ? <span className={styles.landmarkName}>{LANDMARK_LABELS[key].long}</span> : null}
      </div>
    );
  };

  return (
    <div
      ref={viewportRef}
      className={`${styles.viewport} ${modeClass} ${className}`}
      style={viewportStyle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={(e) => finishPointer(e, false)}
      onPointerCancel={(e) => finishPointer(e, true)}
      onContextMenu={(e) => !readOnly && e.preventDefault()}
    >
      <div ref={stageRef} className={styles.stage} style={stageStyle}>
        <img className={styles.image} src={imageUrl} alt={alt} draggable={false} />

        {showAnnotations && ready ? (
          <>
            <svg className={styles.overlay} viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
              {ghost.length >= 3 ? (
                <path
                  d={toPath(ghost)}
                  fill="none"
                  stroke={COLORS.ghost}
                  strokeWidth={1.25}
                  strokeDasharray="4 4"
                  vectorEffect="non-scaling-stroke"
                />
              ) : null}

              {contour.length >= 3 ? (
                <>
                  <path
                    d={toPath(contour)}
                    fill={COLORS.contourFill}
                    stroke={COLORS.halo}
                    strokeWidth={4.5}
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  <path
                    d={toPath(contour)}
                    fill="none"
                    stroke={COLORS.contour}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </>
              ) : null}

              {lines
                ? MEASUREMENT_LINES.map((def) => (
                    <OverlayLine key={def.key} line={lines[def.lineKey]} color={def.color} />
                  ))
                : null}
              {lines && isPoint(lines.center) ? (
                <Handle point={lines.center} r={3.5} unit={unit} fill={COLORS.center} stroke={COLORS.halo} />
              ) : null}

              {isPoint(landmarks.nose) && isPoint(derived.anterior) ? (
                <OverlayLine
                  line={{ start: landmarks.nose, end: derived.anterior }}
                  color={COLORS.landmark}
                  width={1.25}
                  dashed
                />
              ) : null}

              {scalePoints.length === 2 ? (
                <OverlayLine
                  line={{ start: scalePoints[0], end: scalePoints[1] }}
                  color={COLORS.scale}
                  width={2.5}
                />
              ) : null}
              {scalePoints.map((p, i) => (
                <Handle
                  key={`scale-${i}`}
                  point={p}
                  r={scaleActive ? HANDLE_RADIUS.scale : HANDLE_RADIUS.idle + 1}
                  unit={unit}
                  fill={COLORS.scale}
                  stroke={COLORS.contourPoint}
                />
              ))}

              {!readOnly
                ? contour.map((p, i) => {
                    const selected = contourActive && i === selectedPointIndex;
                    let r = HANDLE_RADIUS.idle;
                    if (contourActive) r = selected ? HANDLE_RADIUS.selected : HANDLE_RADIUS.active;
                    return (
                      <Handle
                        key={`c-${i}`}
                        point={p}
                        r={r}
                        unit={unit}
                        fill={selected ? COLORS.selected : COLORS.contourPoint}
                        stroke={selected ? COLORS.contourPoint : COLORS.contour}
                        strokeWidth={contourActive ? 2 : 1}
                      />
                    );
                  })
                : null}

              <Handle
                point={derived.anterior}
                r={HANDLE_RADIUS.derived}
                unit={unit}
                fill={COLORS.derived}
                stroke={COLORS.landmark}
                strokeWidth={1.5}
              />
              <Handle
                point={posteriorPoint}
                r={landmarksActive ? HANDLE_RADIUS.landmark : HANDLE_RADIUS.derived}
                unit={unit}
                fill={posteriorManual ? COLORS.landmark : COLORS.derived}
                stroke={posteriorManual ? COLORS.landmarkText : COLORS.landmark}
                strokeWidth={1.5}
              />

              {LANDMARK_KEYS.map((key) => (
                <Handle
                  key={key}
                  point={landmarks[key]}
                  r={landmarksActive ? HANDLE_RADIUS.landmark : HANDLE_RADIUS.landmarkIdle}
                  unit={unit}
                  fill={COLORS.landmark}
                  stroke={COLORS.landmarkText}
                  strokeWidth={1.5}
                />
              ))}
            </svg>
            {renderLandmarkLabel("anterior", derived.anterior, false)}
            {renderLandmarkLabel("posterior", posteriorPoint, true)}
            {LANDMARK_KEYS.map((key) => renderLandmarkLabel(key, landmarks[key], true))}
          </>
        ) : null}
      </div>
    </div>
  );
}

export default CranialCanvas;
